import { useEffect, useRef } from 'react';

const Hero = () => {
  const containerRef = useRef(null);
  const contentRef = useRef(null);

  useEffect(() => {
    // Simple fade-in on mount using CSS classes
    const content = contentRef.current;
    if (content) {
      content.classList.add('animate-fade-in');
    }
  }, []);

  return (
    <section
      ref={containerRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Static background - no JS animations */}
      <div className="absolute inset-0 z-0">
        {/* Gradient orbs - CSS animations only */}
        <div className="absolute inset-0">
          <div className="absolute top-1/4 left-1/4 w-[300px] h-[300px] sm:w-[400px] sm:h-[400px] md:w-[600px] md:h-[600px] bg-accent-500/20 rounded-full blur-[100px] md:blur-[150px] animate-float" />
          <div className="absolute bottom-1/4 right-1/4 w-[250px] h-[250px] sm:w-[350px] sm:h-[350px] md:w-[500px] md:h-[500px] bg-blue-500/15 rounded-full blur-[80px] md:blur-[130px] animate-float" style={{ animationDelay: '-2s' }} />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[350px] h-[350px] sm:w-[500px] sm:h-[500px] md:w-[700px] md:h-[700px] bg-purple-500/10 rounded-full blur-[100px] md:blur-[160px]" />
        </div>
        
        {/* Grid pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
            `,
            backgroundSize: '60px 60px',
          }}
        />
        
        {/* Radial gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-dark-950/50 to-dark-950" />
      </div>

      {/* Content */}
      <div ref={contentRef} className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 text-center opacity-0 transition-opacity duration-1000 pt-20 md:pt-0">
        {/* Headline */}
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold tracking-tight mb-4 leading-[0.95]">
          <span className="block text-white">
            Never Miss a
          </span>
          <span className="block py-2">
            <span className="gradient-text">Commit Again.</span>
          </span>
        </h1>

        {/* Subheadline */}
        <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-dark-300 max-w-3xl mx-auto mb-8 leading-relaxed px-2 sm:px-0">
          Smart GitHub & GitLab commit notifications without the noise.
          <span className="hidden sm:inline"> </span>
          <br className="hidden sm:block" />
          Stay updated on what matters, skip what doesn't.
        </p>

        {/* CTA Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-5 w-full px-4 sm:px-0">
          <a
            href="https://github.com/Mohammed-3tef/CommitWatch/releases/latest"
            target="_blank"
            rel="noopener noreferrer"
            className="group relative w-full sm:w-auto px-8 sm:px-10 py-4 sm:py-5 bg-accent-500 text-dark-950 font-bold rounded-2xl overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-glow-xl"
          >
            <span className="relative z-10 flex items-center justify-center gap-3 text-base sm:text-lg">
              <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm0 2.824a9.176 9.176 0 110 18.352 9.176 9.176 0 010-18.352zM7.412 12a4.588 4.588 0 119.176 0 4.588 4.588 0 01-9.176 0z"/>
              </svg>
              Install Extension
            </span>
          </a>

          <a
            href="https://github.com/Mohammed-3tef/CommitWatch"
            target="_blank"
            rel="noopener noreferrer"
            className="group w-full sm:w-auto px-8 sm:px-10 py-4 sm:py-5 glass rounded-2xl font-bold text-white border border-white/10 hover:border-accent-500/50 transition-all duration-300 hover:scale-105 hover:shadow-glow-sm"
          >
            <span className="flex items-center justify-center gap-3 text-base sm:text-lg">
              <svg className="w-6 h-6 transition-transform group-hover:rotate-12" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.374 0 0 5.373 0 12c0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23A11.509 11.509 0 0112 5.803c1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576C20.566 21.797 24 17.3 24 12c0-6.627-5.373-12-12-12z"/>
              </svg>
              View on GitHub
            </span>
          </a>
        </div>
      </div>

      {/* Scroll indicator - CSS animation only (hidden on mobile) */}
      <div className="hidden sm:block absolute bottom-10 left-1/2 -translate-x-1/2 z-10">
        <div
          className="flex flex-col items-center gap-3 cursor-pointer group"
          onClick={() => window.scrollTo({ top: window.innerHeight, behavior: 'smooth' })}
        >
          <span className="text-sm text-dark-400 group-hover:text-accent-400 transition-colors">
            Scroll to explore
          </span>
          <div className="w-6 h-10 rounded-full border-2 border-dark-600 group-hover:border-accent-500 transition-colors flex items-start justify-center p-2">
            <div className="w-1.5 h-1.5 bg-accent-500 rounded-full animate-bounce" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
